import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../../config';

/**
 * Base API service for making HTTP requests to the main API
 * Provides convenience methods for GET, POST, PUT, DELETE operations
 *
 * Requests go to `${baseUrl}/${apiVersion}/${endpoint}`; the injected instance uses v1. A repository that needs
 * another version takes its own client with `inject(ApiService).version(2)`. The injected instance is shared by
 * every repository, so its version never changes.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  protected http = inject(HttpClient);
  public baseUrl = inject(API_BASE_URL);
  private injector = inject(Injector);
  private apiVersion = 'v1';

  /**
   * A new client for the given API version (2, '2' or 'v2' all mean v2). This instance is unchanged.
   */
  public version(version: string | number): ApiService {
    const apiVersion = ApiService.normaliseVersion(version);
    // Same class as this one (AdminApiService stays AdminApiService), created in an injection context so its
    // inject() fields resolve.
    const serviceClass = this.constructor as new () => ApiService;
    const client = runInInjectionContext(this.injector, () => new serviceClass());
    client.apiVersion = apiVersion;
    return client;
  }

  /** The versioned base every request is built on, ending in '/'. */
  public get url(): string {
    return `${this.baseUrl}/${this.apiVersion}/`;
  }

  private static normaliseVersion(version: string | number): string {
    const text = String(version).trim().toLowerCase();
    const match = /^v?([1-9][0-9]*)$/.exec(text);
    if (!match) {
      throw new Error(`ApiService.version: '${version}' is not an API version (expected e.g. 2, '2' or 'v2')`);
    }
    return `v${match[1]}`;
  }

  private cleanEndpoint(endpoint: string): string {
    return endpoint.replace(/^\/|\/$/g, '');
  }

  private setHeaders(headers: { [key: string]: string }): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    if (headers === null) { return httpHeaders; }
    for (const key in headers) {
      if (headers.hasOwnProperty(key) && headers[key] !== null && headers[key] !== undefined && headers[key].trim() !== '') {
        httpHeaders = httpHeaders.set(key, headers[key]);
      }
    }
    return httpHeaders;
  }

  private setParams(params: { [key: string]: any }): HttpParams {
    let httpParams = new HttpParams();
    if (params === null) { return httpParams; }
    for (const key in params) {
      if (params.hasOwnProperty(key) && params[key] !== null && params[key] !== undefined) {
        httpParams = httpParams.set(key, params[key].toString());
      }
    }
    return httpParams;
  }

  public get<T>(endpoint: string, params?: { [key: string]: any }, headers?: { [key: string]: string }): Observable<T> {
    const httpHeaders = this.setHeaders(headers || {});
    const httpParams = this.setParams(params || {});
    endpoint = this.cleanEndpoint(endpoint);
    return this.http.get<T>(`${this.url}${endpoint}`, { headers: httpHeaders, params: httpParams });
  }

  public post<T>(endpoint: string, body?: any, params?: { [key: string]: any }, headers?: { [key: string]: string }): Observable<T> {
    const httpHeaders = this.setHeaders(headers || {});
    const httpParams = this.setParams(params || {});
    endpoint = this.cleanEndpoint(endpoint);
    return this.http.post<T>(`${this.url}${endpoint}`, body, { headers: httpHeaders, params: httpParams });
  }

  public put<T>(endpoint: string, body?: any, params?: { [key: string]: any }, headers?: { [key: string]: string }): Observable<T> {
    const httpHeaders = this.setHeaders(headers || {});
    const httpParams = this.setParams(params || {});
    endpoint = this.cleanEndpoint(endpoint);
    return this.http.put<T>(`${this.url}${endpoint}`, body, { headers: httpHeaders, params: httpParams });
  }

  public delete<T>(endpoint: string, params?: { [key: string]: any }, headers?: { [key: string]: string }): Observable<T> {
    const httpHeaders = this.setHeaders(headers || {});
    const httpParams = this.setParams(params || {});
    endpoint = this.cleanEndpoint(endpoint);
    return this.http.delete<T>(`${this.url}${endpoint}`, { headers: httpHeaders, params: httpParams });
  }

  public request<T>(method: string, endpoint: string, options?: { body?: any; params?: { [key: string]: any }; headers?: { [key: string]: string } }): Observable<T> {
    const httpHeaders = this.setHeaders(options?.headers || {});
    const httpParams = this.setParams(options?.params || {});
    endpoint = this.cleanEndpoint(endpoint);
    return this.http.request<T>(method, `${this.url}${endpoint}`, { body: options?.body, headers: httpHeaders, params: httpParams });
  }
}
